import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { actions, gameStateType } from "core/constants";
import { TextStyle } from "components/Text";
import Character from "components/CharacterWeb";
import GameText from "containers/Game/GameText";
import { SelectableLink, SelectableLinkContainer } from "components/Link";
import { lightBlue } from "core/styles";

const { STARTED, NIGHT, MORNING, DAY, POLLS, VAMPIRE_WIN, HUMAN_WIN } =
  gameStateType;

const { GAME_HISTORY_UPDATE, FIND_SESSION } = actions;

const Wrapper = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 2vw;
`;

const CharactersWrapper = styled.div`
  display: flex;
  width: 100%;
  margin: 3vw auto;
  align-items: center;
  justify-content: space-around;
  flex-wrap: wrap;
`;

const Text = styled(TextStyle)`
  margin-top: 1vw;
  font-size: 3vw;
`;

const DeadPlayerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 1vw;
`;

const PlayerName = styled(TextStyle)`
  font-size: 2.5vw;
  margin-top: 1vw;
`;

const List = styled.div`
  margin-top: 1vw;
`;

const Item = styled(TextStyle)`
  font-size: 2.8vw;
  margin: 0.5vw 0;
  text-decoration: ${({ lineThrough }) =>
    lineThrough ? "line-through" : "none"};
`;

// Style copied from const backButtonStyles = css` in SelectableLink.js
const StyledSelectableLinkContainer = styled(SelectableLinkContainer)`
  position: fixed;
  top: 3vw;
  left: 3vw;
`;

export const OpenGameHistoryButton = ({ onClick }) => {
  return (
    <StyledSelectableLinkContainer>
      <SelectableLink onClick={onClick}>Archives</SelectableLink>
    </StyledSelectableLinkContainer>
  );
};

const processRounds = (eventLog) => {
  // Process events into rounds
  const rounds = [];
  let prevAliveIds = new Set();

  eventLog.forEach((event) => {
    const eventName = event.action.split("-").slice(1).join("-");

    if (eventName === "ROLES_ASSIGNED") {
      rounds.push({ type: "roles", event });
      return;
    }

    if (eventName === "updated-session-game-state") {
      const { gameState, message, players, roundNumber, personalNote, polls } =
        event.payload;

      // Find new dead players (those who are expired now, but were not expired before)
      const currentDead = players?.filter((p) => p.expired) || [];
      const currentAlive = players?.filter((p) => !p.expired) || [];
      // const currentDeadIds = new Set(currentDead.map((p) => p.id));
      const newDeadPlayers = currentDead.filter(
        (p) => prevAliveIds.has(p.id) || prevAliveIds.size === 0
      );

      // Update prevAliveIds for next round
      prevAliveIds = new Set(currentAlive.map((p) => p.id));

      const aliveCount = currentAlive.length || 0;

      if (gameState === "human-win" || gameState === "vampire-win") {
        rounds.push({ type: "endgame", gameState, message });
      } else {
        rounds.push({
          type: "round",
          gameState,
          roundNumber,
          message,
          deadPlayers: newDeadPlayers,
          aliveCount,
          players,
          personalNote,
          polls,
        });
      }
    }

    if (eventName === "ended") {
      rounds.push({ type: "ended" });
    }
  });

  return rounds;
};

// Custom hook to fetch and subscribe to game event logs
export function useGameEventLog(socket, gameSessionId, player, gameState) {
  const [eventLog, setEventLog] = useState([]);
  const hasFetchedRef = React.useRef(false);

  useEffect(() => {
    if (!socket || !gameSessionId) {
      return;
    }

    const handleHistoryUpdate = (data) => {
      setEventLog(data.eventLog || []);
    };

    // Listen for history updates
    socket.on(GAME_HISTORY_UPDATE, handleHistoryUpdate);

    // Only emit FIND_SESSION once per session
    if (!hasFetchedRef.current && player?.id) {
      socket.emit(FIND_SESSION, { gameSessionId, playerId: player.id });
      hasFetchedRef.current = true;
    }

    return () => {
      socket.off(GAME_HISTORY_UPDATE, handleHistoryUpdate);
    };
  }, [socket, gameSessionId, player?.id]);

  const shouldShowHistory = [
    STARTED,
    NIGHT,
    MORNING,
    DAY,
    POLLS,
    VAMPIRE_WIN,
    HUMAN_WIN,
  ].includes(gameState);

  const processedRounds = processRounds(eventLog);

  return [processedRounds, shouldShowHistory];
}

// --- Card Components for each round type ---

const RolesCard = ({ round }) => {
  const { roleDistribution } = round.event.payload;
  return (
    <GameText title="The Setup" isLong>
      <List>
        {Object.entries(roleDistribution || {}).map(([role, count]) => (
          <Item key={role} white>
            - {role}: {count}
          </Item>
        ))}
      </List>
      <CharactersWrapper>
        {Object.entries(roleDistribution || {}).map(([role, count]) => (
          <Character key={role} role={role} scale={1.2} />
        ))}
      </CharactersWrapper>
    </GameText>
  );
};

const StartedCard = ({ round }) => (
  <GameText title="Who's who?" isLong>
    {round.personalNote && <Text>{round.personalNote}</Text>}
    {round.message && <Text white>{round.message}</Text>}
    {round.players && (
      <List>
        {round.players.map((player) => (
          <Item key={player.id} lineThrough={player.expired} white>
            - {player.userName}
          </Item>
        ))}
      </List>
    )}
  </GameText>
);

const NightCard = ({ round }) => (
  <GameText title={`Night ${round.roundNumber || ""}`} isLong>
    {round.personalNote && <Text>{round.personalNote}</Text>}
    {round.message && <Text white>{round.message}</Text>}
    {/* Dead players for night */}
    {round.deadPlayers.length > 0 && (
      <>
        <CharactersWrapper>
          {round.deadPlayers
            .filter((p) => p.image && p.role)
            .map((player) => (
              <DeadPlayerWrapper key={player.id}>
                <Character role={player.image} isExpired={true} scale={1.2} />
                <PlayerName white>{player.userName}</PlayerName>
              </DeadPlayerWrapper>
            ))}
        </CharactersWrapper>
        {round.deadPlayers.filter((p) => !p.image || !p.role).length > 0 && (
          <Text white style={{ fontSize: "2.5vw", color: "#aaa" }}>
            Other deaths:{" "}
            {round.deadPlayers
              .filter((p) => !p.image || !p.role)
              .map((p) => p.userName)
              .join(", ")}
          </Text>
        )}
      </>
    )}
    {round.aliveCount > 0 && (
      <Text
        white
        style={{ marginTop: "1vw", fontSize: "2.5vw", color: "#888" }}
      >
        Remaining: {round.aliveCount}
      </Text>
    )}
  </GameText>
);

const MorningCard = ({ round }) => (
  <GameText title={`Morning ${round.roundNumber || ""}`} isLong>
    {round.personalNote && <Text>{round.personalNote}</Text>}
    {round.message && <Text white>{round.message}</Text>}
    {/* Dead players for morning */}
    {round.deadPlayers.length > 0 && (
      <>
        <CharactersWrapper>
          {round.deadPlayers
            .filter((p) => p.image && p.role)
            .map((player) => (
              <DeadPlayerWrapper key={player.id}>
                <Character role={player.image} isExpired={true} scale={1.2} />
                <PlayerName white>{player.userName}</PlayerName>
              </DeadPlayerWrapper>
            ))}
        </CharactersWrapper>
        {round.deadPlayers.filter((p) => !p.image || !p.role).length > 0 && (
          <Text white style={{ fontSize: "2.5vw", color: "#aaa" }}>
            Other deaths:{" "}
            {round.deadPlayers
              .filter((p) => !p.image || !p.role)
              .map((p) => p.userName)
              .join(", ")}
          </Text>
        )}
      </>
    )}
    {round.aliveCount > 0 && (
      <Text
        white
        style={{ marginTop: "1vw", fontSize: "2.5vw", color: "#888" }}
      >
        Remaining: {round.aliveCount}
      </Text>
    )}
  </GameText>
);

const DayCard = ({ round }) => {
  // No card if no for now... may add later!
  return null;
  return (
    <GameText title={`Day ${round.roundNumber || ""}`} isLong>
      {round.personalNote && <Text>{round.personalNote}</Text>}
      {round.message && <Text white>{round.message}</Text>}
      {round.aliveCount > 0 && (
        <Text
          white
          style={{ marginTop: "1vw", fontSize: "2.5vw", color: "#888" }}
        >
          Remaining: {round.aliveCount}
        </Text>
      )}
    </GameText>
  );
};

const SemiStyledResults = styled.div`
  flex: 1;
  font-size: 0.5em;
  align-items: center;
  display: flex;
`;

// TODO: Fix this BS.  I've really given up on the styling
const DeathVote = styled.span`
  border: 1px dashed ${lightBlue};
  padding: 10px;
`;

const ShowVotes = ({ polls = [] }) => {
  return polls.map(({ userName, votes = [], id }) => {
    // const numberOfVotes = votes.length;
    const stakeText = userName ? ` ${userName}:` : "Didn't vote:";
    return (
      <SemiStyledResults key={id}>
        {stakeText}
        {votes.map((votedToKill) => (
          <DeathVote key={votedToKill}>{votedToKill}</DeathVote>
        ))}
      </SemiStyledResults>
    );
  });
};

const PollsCard = ({ round }) => {
  const { polls } = round;
  return (
    <GameText title={`Polls ${round.roundNumber || ""}`} isLong>
      {round.personalNote && <Text>{round.personalNote}</Text>}
      <ShowVotes polls={polls} />
      {round.message && <Text white>{round.message}</Text>}
      {/* Dead players for polls */}
      {round.deadPlayers.length > 0 && (
        <>
          <CharactersWrapper>
            {round.deadPlayers
              .filter((p) => p.image && p.role)
              .map((player) => (
                <DeadPlayerWrapper key={player.id}>
                  <Character role={player.image} isExpired={true} scale={1.2} />
                  <PlayerName white>{player.userName}</PlayerName>
                </DeadPlayerWrapper>
              ))}
          </CharactersWrapper>
          {round.deadPlayers.filter((p) => !p.image || !p.role).length > 0 && (
            <Text white style={{ fontSize: "2.5vw", color: "#aaa" }}>
              Other deaths:{" "}
              {round.deadPlayers
                .filter((p) => !p.image || !p.role)
                .map((p) => p.userName)
                .join(", ")}
            </Text>
          )}
        </>
      )}
      {round.aliveCount > 0 && (
        <Text
          white
          style={{ marginTop: "1vw", fontSize: "2.5vw", color: "#888" }}
        >
          Remaining: {round.aliveCount}
        </Text>
      )}
    </GameText>
  );
};

const EndgameCard = ({ round }) => {
  const title =
    round.gameState === "human-win" ? "Humans Win!" : "Vampires Win!";
  return (
    <GameText title={title} isLong>
      {round.message && <Text white>{round.message}</Text>}
    </GameText>
  );
};

const EndedCard = () => (
  <GameText title="Game Ended" isLong>
    <Text white>The game was ended by the host.</Text>
  </GameText>
);

// --- Refactored EventDetail ---

export const EventDetail = ({ round }) => {
  // Special cards for roles, endgame, ended
  if (round.type === "roles") {
    return <RolesCard round={round} />;
  }
  if (round.type === "endgame") {
    return <EndgameCard round={round} />;
  }
  if (round.type === "ended") {
    return <EndedCard />;
  }

  // All other rounds: use gameState
  switch (round.gameState) {
    case STARTED:
      return <StartedCard round={round} />;
    case NIGHT:
      return <NightCard round={round} />;
    case MORNING:
      return <MorningCard round={round} />;
    case DAY:
      return <DayCard round={round} />;
    case POLLS:
      return <PollsCard round={round} />;
    default:
      // fallback for unknown/legacy states
      return null;
  }
};

export default function GameHistoryMobile({ eventLog, onClickBack }) {
  return (
    <Wrapper>
      <SelectableLinkContainer>
        <SelectableLink to="/" onClick={onClickBack} backButton />
      </SelectableLinkContainer>
      <GameText title="Archives" />

      {eventLog.length === 0 && (
        // THis should NEVER happen - but claude added it
        <GameText title="No Events Yet" isLong>
          <Text white>The game history will appear here as events occur.</Text>
        </GameText>
      )}

      {eventLog.map((round, idx) => (
        <EventDetail key={idx} round={round} />
      ))}
    </Wrapper>
  );
}
